/**
 * RegisterTicketModal - 대시보드 단축 "수강권 등록" 모달 (instructor-web)
 *
 * MemberDetailPage의 PurchaseTicketModal과 달리, 회원을 먼저 검색·선택한 뒤
 * 카탈로그 수강권을 골라 판매한다(대시보드에서 회원 상세를 거치지 않는 단축 동선).
 *
 * - 회원 목록: getMembersList (강사 read 권한)
 * - 카탈로그: getTicketCatalogList (강사 read 권한)
 * - 등록: usePurchaseTicket → Cloud Function `instructorPurchaseTicket`
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, Button, Alert, Spinner } from "@vlab-corporation/design-system";
import { usePurchaseTicket } from "@/hooks/useTicketPurchase";
import { getTicketCatalogList, type TicketCatalogItem } from "@/services/firestore/ticketCatalog";
import { getMembersList } from "@/services/firestore/members";
import type { Member } from "@vlab-corporation/vlab-pilates-service-interface/customer";

interface RegisterTicketModalProps {
  isOpen: boolean;
  ownerUid: string;
  centerId: number;
  centerName: string;
  onClose: () => void;
  onCreated: () => void;
}

function getErrorMessage(error: unknown): string | null {
  if (error && typeof error === "object" && "message" in error) {
    const msg = (error as { message: unknown }).message;
    if (typeof msg === "string" && msg.length > 0) return msg;
  }
  return null;
}

function formatCatalogOption(item: TicketCatalogItem): string {
  const parts: string[] = [`${item.title} · ${item.type}`, `${item.totalCoupon}회`];
  if (item.validDays !== undefined) parts.push(`${item.validDays}일`);
  if (item.maxTrainee !== undefined) parts.push(`최대 ${item.maxTrainee}인`);
  return parts.join(" · ");
}

export function RegisterTicketModal({
  isOpen,
  ownerUid,
  centerId,
  centerName,
  onClose,
  onCreated,
}: RegisterTicketModalProps) {
  const [search, setSearch] = useState("");
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [selectedTicketId, setSelectedTicketId] = useState("");
  const [catalogs, setCatalogs] = useState<TicketCatalogItem[]>([]);
  const [isLoadingCatalogs, setIsLoadingCatalogs] = useState(false);
  const [catalogLoadError, setCatalogLoadError] = useState<string | null>(null);

  const purchaseTicket = usePurchaseTicket();
  const queryClient = useQueryClient();

  const { data: members, isLoading: membersLoading } = useQuery({
    queryKey: ["membersList", ownerUid, centerId],
    queryFn: () => getMembersList(ownerUid, centerId),
    staleTime: 30 * 60 * 1000,
    enabled: isOpen && !!ownerUid && !!centerId,
  });

  // 모달 open 시 상태 초기화 + 카탈로그 로드
  useEffect(() => {
    if (!isOpen) return;

    setSearch("");
    setSelectedMember(null);
    setSelectedTicketId("");
    setCatalogLoadError(null);
    purchaseTicket.reset();

    let cancelled = false;
    setIsLoadingCatalogs(true);
    getTicketCatalogList(ownerUid, centerId)
      .then((items) => {
        if (!cancelled) setCatalogs(items);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setCatalogLoadError(
            err instanceof Error ? err.message : "수강권 카탈로그를 불러오지 못했습니다.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingCatalogs(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, ownerUid, centerId]);

  const filteredMembers = useMemo(() => {
    if (!members) return [];
    const q = search.trim().toLowerCase();
    const list = q ? members.filter((m) => m.name.toLowerCase().includes(q)) : members;
    return [...list].sort((a, b) => a.name.localeCompare(b.name)).slice(0, 50);
  }, [members, search]);

  if (!isOpen) return null;

  const handleRegister = () => {
    if (!selectedMember || !selectedTicketId) return;
    const memberId = selectedMember.id;
    purchaseTicket.mutate(
      {
        centerName,
        memberName: selectedMember.name,
        memberMobile: selectedMember.contact ?? "",
        ticketId: selectedTicketId,
      },
      {
        onSuccess: () => {
          // 해당 회원 상세의 보유 수강권 캐시를 무효화해 stale 데이터를 방지한다.
          void queryClient.invalidateQueries({
            queryKey: ["memberTickets", ownerUid, centerId, memberId],
          });
          onCreated();
        },
      },
    );
  };

  const serverErrorMessage = purchaseTicket.isError ? getErrorMessage(purchaseTicket.error) : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
      <Card className="w-full max-w-md p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">수강권 등록</h2>
        <p className="text-sm text-gray-500 mb-4">센터: {centerName}</p>

        {/* 1단계: 회원 선택 */}
        {!selectedMember ? (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-1">회원 선택</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="이름 검색..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-2 focus:outline-none focus:border-primary-400"
            />
            {membersLoading ? (
              <div className="flex justify-center py-6">
                <Spinner size="md" />
              </div>
            ) : filteredMembers.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center">
                {search ? `"${search}" 검색 결과가 없습니다.` : "등록된 회원이 없습니다."}
              </p>
            ) : (
              <div className="max-h-56 overflow-y-auto space-y-1 mb-2">
                {filteredMembers.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    disabled={!m.contact}
                    onClick={() => m.contact && setSelectedMember(m)}
                    className="w-full text-left px-3 py-2 rounded-lg border border-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed enabled:hover:bg-gray-50 enabled:active:bg-gray-100"
                  >
                    <span className="text-sm font-medium text-gray-800">{m.name}</span>
                    {m.contact ? (
                      <span className="text-xs text-gray-400 ml-2">{m.contact}</span>
                    ) : (
                      <span className="text-xs text-red-400 ml-2">연락처 없음 · 등록 불가</span>
                    )}
                  </button>
                ))}
              </div>
            )}
            <div className="flex justify-end pt-2">
              <Button type="button" variant="outline" onClick={onClose}>
                취소
              </Button>
            </div>
          </>
        ) : (
          /* 2단계: 수강권 선택 */
          <>
            <div className="mb-4 p-3 bg-gray-50 rounded-md text-sm flex items-center justify-between">
              <span>
                <span className="font-medium text-gray-500">회원:</span>{" "}
                <span className="text-gray-800">{selectedMember.name}</span>
                {selectedMember.contact && (
                  <span className="text-gray-400 ml-2">{selectedMember.contact}</span>
                )}
              </span>
              <button
                type="button"
                onClick={() => {
                  setSelectedMember(null);
                  setSelectedTicketId("");
                }}
                className="text-xs text-primary-500 hover:underline flex-shrink-0"
              >
                변경
              </button>
            </div>

            <div className="mb-4">
              <label
                htmlFor="registerTicketCatalog"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                수강권 <span className="text-red-500">*</span>
              </label>
              {isLoadingCatalogs ? (
                <p className="text-sm text-gray-500">카탈로그를 불러오는 중...</p>
              ) : catalogLoadError ? (
                <p className="text-sm text-red-600">{catalogLoadError}</p>
              ) : catalogs.length === 0 ? (
                <p className="text-sm text-gray-600">
                  등록된 수강권이 없습니다. 원장에게 수강권 카탈로그 등록을 요청하세요.
                </p>
              ) : (
                <select
                  id="registerTicketCatalog"
                  value={selectedTicketId}
                  onChange={(e) => setSelectedTicketId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:border-primary-400"
                >
                  <option value="">수강권 선택</option>
                  {catalogs.map((item) => (
                    <option key={item.id} value={item.id}>
                      {formatCatalogOption(item)}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {purchaseTicket.isError && (
              <Alert variant="error" className="mb-4">
                {serverErrorMessage ?? "수강권 등록에 실패했습니다. 다시 시도해주세요."}
              </Alert>
            )}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                취소
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleRegister}
                disabled={!selectedTicketId || purchaseTicket.isPending}
              >
                {purchaseTicket.isPending ? "등록 중..." : "등록"}
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
